import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { BUSINESS } from "@/data/site";
import {
  LayoutDashboard,
  CalendarDays,
  Users,
  Tags,
  FileText,
  ReceiptText,
  TriangleAlert,
  LogOut,
  Menu,
  X,
  HeartHandshake,
} from "lucide-react";

const ADMIN_NAV = [
  { to: "/portal/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/portal/planner", label: "Planner", icon: CalendarDays },
  { to: "/portal/clients", label: "Clients", icon: Users },
  { to: "/portal/incidents", label: "Incidents", icon: TriangleAlert },
  { to: "/portal/services", label: "Services & Pricing", icon: Tags },
  { to: "/portal/invoices", label: "Invoices", icon: FileText },
  { to: "/portal/receipts", label: "Receipts", icon: ReceiptText },
];

const FAMILY_NAV = [{ to: "/portal/family", label: "Care Overview", icon: HeartHandshake }];

export const PortalLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const nav = user?.role === "admin" ? ADMIN_NAV : FAMILY_NAV;

  const handleLogout = () => {
    logout();
    navigate("/portal/login");
  };

  const SidebarInner = () => (
    <div className="flex flex-col h-full">
      <div className="px-6 py-6 border-b border-charcoal/10">
        <img src={BUSINESS.logo} alt="HomeCare by Nicole" className="h-16 w-auto object-contain" />
      </div>
      <nav className="flex-1 px-4 py-6 space-y-1">
        {nav.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            onClick={() => setOpen(false)}
            data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-xl px-4 py-3 text-sm transition-colors ${
                isActive
                  ? "bg-sage text-white font-medium"
                  : "text-charcoal-soft hover:bg-sand-deep hover:text-charcoal"
              }`
            }
          >
            <item.icon className="h-5 w-5" />
            {item.label}
          </NavLink>
        ))}
      </nav>
      <div className="px-4 py-5 border-t border-charcoal/10">
        <div className="px-2 mb-3">
          <p className="text-sm font-medium text-charcoal truncate">{user?.name}</p>
          <p className="text-xs text-charcoal-soft capitalize">{user?.role}</p>
        </div>
        <button
          onClick={handleLogout}
          data-testid="logout-button"
          className="w-full flex items-center gap-3 rounded-xl px-4 py-3 text-sm text-charcoal-soft hover:bg-destructive/10 hover:text-destructive transition-colors"
        >
          <LogOut className="h-5 w-5" /> Sign out
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-sand flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-72 shrink-0 bg-white border-r border-charcoal/10 flex-col fixed inset-y-0 left-0">
        <SidebarInner />
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-30 bg-white border-b border-charcoal/10 h-16 flex items-center justify-between px-4">
        <img src={BUSINESS.logo} alt="logo" className="h-11 w-auto object-contain" />
        <button onClick={() => setOpen(true)} data-testid="mobile-menu-open" className="p-2 text-charcoal">
          <Menu className="h-6 w-6" />
        </button>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-40">
          <div className="absolute inset-0 bg-charcoal/40" onClick={() => setOpen(false)} />
          <div className="absolute left-0 inset-y-0 w-72 bg-white">
            <button onClick={() => setOpen(false)} className="absolute top-5 right-4 p-2 text-charcoal z-10">
              <X className="h-5 w-5" />
            </button>
            <SidebarInner />
          </div>
        </div>
      )}

      {/* Content */}
      <main className="flex-1 lg:ml-72 pt-16 lg:pt-0 min-h-screen">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8 sm:py-12">
          <Outlet />
        </div>
      </main>
    </div>
  );
};
